import { useEffect, useMemo, useState } from 'react';
import { createUser, getUsersByOrganization, updateUser, deleteUser as deleteUserApi } from 'src/api/user';
import { useOrganizationContext } from 'src/hooks/use-organization-context';
import { User } from 'src/models/user';
import { UserContext } from './user-context-types';

interface UserProviderProps {
  children: React.ReactNode;
}

const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { currentOrg } = useOrganizationContext();
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (currentOrg) {
      getUsersByOrganization(currentOrg).then((res) => {
        if (res.success) {
          setUsers(res.data);
        }
      });
    }
  }, [currentOrg]);

  const providerValue = useMemo(() => {
    const addUser = async (user: User): Promise<null | string> => {
      const res = await createUser(user);
      if (res.success) {
        setUsers([...users, res.data]);
        return null;
      }
      return res.error ?? 'Failed to create user';
    };

    const editUser = (user: Partial<User> & { _id: string }): void => {
      updateUser(user).then((res) => {
        if (res.success && res.data) {
          const dept = res.data;
          const updated = users.map((d) => (d._id === dept._id ? dept : d));
          setUsers(updated);
        }
      });
    };

    const deleteUser = async (userId: string): Promise<string | null> => {
      const res = await deleteUserApi(userId);
      if (res.success) {
        const filtered = users.filter((d) => d._id !== userId);
        setUsers(filtered);
        return null;
      }
      return res.error ?? 'Failed to delete user';
    };

    return { users, setUsers, addUser, editUser, deleteUser };
  }, [users]);

  return <UserContext.Provider value={providerValue}>{children}</UserContext.Provider>;
};

export default UserProvider;
